import React, { ReactElement } from "react";
import i18next, { useTranslation } from "utilities/i18n";
import { getInsightTitleTranslation } from "utilities/insights";
import { FilterPanel } from "collections/FilterPanel";
import { InsightList } from "collections/InsightDetails";
import usePriorityInsight from "./usePriorityInsight";
import {
  InsightFilter,
  areInsightFiltersSelected,
  FilterOption,
  LoadingIndicator,
  CategorySwitcher,
  CategoryDiv
} from "elements";
import {
  InsightFilterState,
  SetInsightFilter,
  usePriorityFilter
} from "./QueryParams";
import { Layout } from "views/Main/Layouts";
import {
  Category,
  Device,
  LocationScope,
  Room,
  Site,
  Tenant,
  useTopicsQuery
} from "generated/graphql";
import { handleNullFields } from "utilities/stringUtils";
import { useSelector } from "react-redux";
import { State } from "store";
import { OptimizelyFeature } from "@optimizely/react-sdk";

interface GroupByData {
  locationScope: LocationScope;
  device: Pick<Device, "name" | "id"> | null;
  deviceType: string | null;
  model: string | null;
  modelVersion: string | null;
  family: string | null;
  version: string | null;
  room: Pick<Room, "name" | "id"> | null;
  site: Pick<Site, "name" | "id"> | null;
  tenant: Pick<Tenant, "name" | "id"> | null;
}

export const constructScopeFilter = (data: GroupByData) => {
  let scopeFilter: any = {};
  switch (data.locationScope) {
    case LocationScope.DeviceType:
      scopeFilter["deviceType"] = data.deviceType;
      break;
    case LocationScope.Model:
      scopeFilter["model"] = data.model;
      break;
    case LocationScope.ModelVersion:
      const modelVersionOptions = {
        model: data.model,
        version: data.version
      };
      scopeFilter = modelVersionOptions;
      break;
    case LocationScope.Room:
      scopeFilter["room"] = data.room ? data.room.name : "";
      break;
    case LocationScope.Site:
      scopeFilter["site"] = data.site ? data.site.name : "";
      break;
    case LocationScope.Version:
      scopeFilter["version"] = data.version;
      break;
  }
  return scopeFilter;
};

export const getDataGroupByParam = (data: GroupByData) => {
  let groupByParam: string | null;
  switch (data.locationScope) {
    case LocationScope.Device:
      groupByParam = handleNullFields(data.device?.name);
      break;
    case LocationScope.DeviceType:
      groupByParam = data.deviceType;
      break;
    case LocationScope.Family:
      groupByParam = data.family;
      break;
    case LocationScope.Model:
      groupByParam = data.model;
      break;
    case LocationScope.ModelVersion:
      const modelVersionOptions = {
        model:
          data.model ??
          i18next.t("UNKNOWN_GROUP_BY", {
            groupBy: i18next.t(data.locationScope)
          }),
        version: data.version
      };
      groupByParam = data.version
        ? i18next.t("MODEL_VERSION_INSIGHT", modelVersionOptions)
        : i18next.t("MODEL_UNKNOWN_VERSION_INSIGHT", modelVersionOptions);
      break;
    case LocationScope.Room:
      groupByParam = handleNullFields(data.room?.name);
      break;
    case LocationScope.Site:
      groupByParam = handleNullFields(data.site?.name);
      break;
    case LocationScope.Tenant:
      groupByParam = handleNullFields(data.tenant?.name);
      break;
    case LocationScope.Version:
      groupByParam = data.version;
      break;
    default:
      groupByParam = null;
  }
  return (
    groupByParam ??
    i18next.t("UNKNOWN_GROUP_BY", { groupBy: i18next.t(data.locationScope) })
  );
};

export const InsightsCategorySwitcher: React.FC<{
  filterState: InsightFilterState;
  setFilter: SetInsightFilter;
  className?: string;
}> = ({ filterState, setFilter, className }) => {
  return (
    <CategorySwitcher className={`insights ${className}`}>
      <CategoryDiv
        label={i18next.t("ALL")}
        active={!filterState.category}
        onClick={() => {
          setFilter({
            ...filterState,
            category: undefined
          });
        }}
      />
      <CategoryDiv
        label={i18next.t("DEVICES")}
        active={filterState.category === Category.Device}
        onClick={() => {
          setFilter({
            ...filterState,
            category: Category.Device
          });
        }}
      />
      <CategoryDiv
        label={i18next.t("ROOMS")}
        active={filterState.category === Category.Room}
        onClick={() => {
          setFilter({
            ...filterState,
            category: Category.Room
          });
        }}
      />
    </CategorySwitcher>
  );
};

const PriorityInsightsLoader: React.FC = () => (
  <OptimizelyFeature feature="room-insights-categories">
    {stageEnabled => (
      <OptimizelyFeature feature="poly-users-flag">
        {audienceEnabled => {
          if (stageEnabled || audienceEnabled) {
            return <PriorityInsights premium />;
          }
          return <PriorityInsights />;
        }}
      </OptimizelyFeature>
    )}
  </OptimizelyFeature>
);

export const PriorityInsights: React.FC<{
  premium?: boolean;
}> = ({ premium = false }) => {
  const { t } = useTranslation();
  const cursor = useSelector((state: State) => state.paging.insightsCursor);
  const [filterState, setFilter, resetFilters] = usePriorityFilter();
  let { data, fetching, stale } = usePriorityInsight(
    filterState,
    premium,
    cursor
  );

  let { data: topicsData, fetching: fetchingTopics } = useTopicsQuery();
  let topicOptions = [] as Array<FilterOption>;

  if (topicsData && !fetchingTopics) {
    const topics = topicsData?.topics.map(topic => ({
      ...topic,
      name: getInsightTitleTranslation(topic.id)
    }));
    if (!premium) {
      topicOptions = topics
        .filter(topic => topic.category === Category.Device)
        .sort(
          (
            a: { id: string; name: string },
            b: { id: string; name: string }
          ) => {
            return a.name.localeCompare(b.name, undefined, {
              sensitivity: "base"
            });
          }
        )
        .map(topic => ({
          text: topic.name,
          value: topic.id
        }));
    } else if (filterState.category) {
      topicOptions = topics
        .filter(topic => topic.category === filterState.category)
        .sort(
          (
            a: { id: string; name: string },
            b: { id: string; name: string }
          ) => {
            return a.name.localeCompare(b.name, undefined, {
              sensitivity: "base"
            });
          }
        )
        .map(topic => ({
          text: topic.name,
          value: topic.id
        }));
    } else {
      topicOptions = topics
        .sort(
          (
            a: { id: string; name: string },
            b: { id: string; name: string }
          ) => {
            return a.name.localeCompare(b.name, undefined, {
              sensitivity: "base"
            });
          }
        )
        .map(topic => ({
          text: topic.name,
          value: topic.id
        }));
    }
  }

  const filters: Array<ReactElement> = [
    <InsightFilter
      title={t("GROUP_BY")}
      field="locationScope"
      filterValues={filterState}
      options={[
        { text: "Site", value: LocationScope.Site },
        { text: "Model", value: LocationScope.Model },
        { text: "Version", value: LocationScope.Version }
      ]}
      onChange={value =>
        setFilter({
          ...filterState,
          locationScope: value !== "" ? value : undefined
        })
      }
      loading={fetching}
      search
      clearable
    />,
    <InsightFilter
      title={t("INSIGHT")}
      field="insight"
      filterValues={filterState}
      options={topicOptions}
      onChange={value =>
        setFilter({
          ...filterState,
          insight: value !== "" ? value : undefined
        })
      }
      loading={fetchingTopics}
      search
      clearable
    />
  ];

  const { Sidebar, Content } = Layout;

  return (
    <Layout>
      <Sidebar>
        <FilterPanel
          filters={filters}
          filterValues={filterState}
          metric={{
            type: t("PRIORITY_INSIGHTS"),
            count: data.total
          }}
          resetFilters={resetFilters}
          areFiltersSelected={areInsightFiltersSelected}
          subHeader={t("PRIORITY_INSIGHTS_DESCRIPTION")}
          data-testid="priority-insights-total-count"
        />
      </Sidebar>
      <Content scrolling>
        {premium && (
          <div className="switcher-container">
            <InsightsCategorySwitcher
              filterState={filterState}
              setFilter={setFilter}
            />
          </div>
        )}
        <Content.ScrollingList data-testid="expandable-card-test">
          {!fetching && <InsightList insightList={data} />}
          {(stale || fetching) && <LoadingIndicator />}
        </Content.ScrollingList>
      </Content>
    </Layout>
  );
};

export default PriorityInsightsLoader;
